import { useState, useCallback } from "react";
import {
  fetchTopics,
  fetchTopicDetail,
  createTopic,
  deleteTopic,
  createReply,
  deleteReply,
} from "../services/communityService";

export function useCommunity() {
  const [activeTab, setActiveTab] = useState("stats"); // stats | topics | chat
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsPage, setTopicsPage] = useState(1);
  const [hasMoreTopics, setHasMoreTopics] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadTopics = useCallback(async (category, page = 1, append = false) => {
    setTopicsLoading(true);
    setError(null);
    try {
      const data = await fetchTopics(category, page);
      if (append) {
        setTopics(prev => [...prev, ...data]);
      } else {
        setTopics(data);
      }
      setHasMoreTopics(data.length >= 20);
      setTopicsPage(page);
    } catch {
      setError("Không thể tải danh sách chủ đề");
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  const loadMoreTopics = useCallback(async () => {
    const nextPage = topicsPage + 1;
    await loadTopics(categoryFilter, nextPage, true);
  }, [topicsPage, categoryFilter, loadTopics]);

  const changeCategory = useCallback(async (cat) => {
    setCategoryFilter(cat);
    await loadTopics(cat, 1, false);
  }, [loadTopics]);

  const loadTopicDetail = useCallback(async (topicId) => {
    setTopicLoading(true);
    setError(null);
    try {
      const data = await fetchTopicDetail(topicId);
      setSelectedTopic(data);
    } catch {
      setError("Không thể tải chủ đề");
    } finally {
      setTopicLoading(false);
    }
  }, []);

  const handleCreateTopic = useCallback(async (title, body, category) => {
    setSubmitting(true);
    setError(null);
    try {
      const newTopic = await createTopic(title, body, category);
      if (newTopic) {
        setTopics(prev => [newTopic, ...prev]);
        setShowComposer(false);
      }
      return newTopic;
    } catch (e) {
      if (e.message === "rate_limit") {
        setError("Bạn đã tạo quá nhiều chủ đề. Vui lòng thử lại sau.");
      } else {
        setError("Không thể tạo chủ đề");
      }
      return null;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const handleDeleteTopic = useCallback(async (topicId) => {
    try {
      const ok = await deleteTopic(topicId);
      if (ok) {
        setTopics(prev => prev.filter(t => t.id !== topicId));
        setSelectedTopic(null);
      }
    } catch {
      setError("Không thể xóa chủ đề");
    }
  }, []);

  const handleCreateReply = useCallback(async (topicId, body) => {
    setSubmitting(true);
    setError(null);
    try {
      const reply = await createReply(topicId, body);
      if (reply && selectedTopic) {
        setSelectedTopic(prev => ({
          ...prev,
          replies: [...(prev.replies || []), reply],
          reply_count: (prev.reply_count || 0) + 1,
        }));
        // Update reply count in topics list
        setTopics(prev => prev.map(t =>
          t.id === topicId ? { ...t, reply_count: (t.reply_count || 0) + 1 } : t
        ));
      }
      return reply;
    } catch (e) {
      if (e.message === "rate_limit") {
        setError("Bạn đã gửi quá nhiều bình luận. Vui lòng thử lại sau.");
      } else {
        setError("Không thể gửi bình luận");
      }
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [selectedTopic]);

  const handleDeleteReply = useCallback(async (replyId, topicId) => {
    try {
      const ok = await deleteReply(replyId);
      if (ok && selectedTopic) {
        setSelectedTopic(prev => ({
          ...prev,
          replies: prev.replies.filter(r => r.id !== replyId),
          reply_count: Math.max((prev.reply_count || 0) - 1, 0),
        }));
        setTopics(prev => prev.map(t =>
          t.id === topicId ? { ...t, reply_count: Math.max((t.reply_count || 0) - 1, 0) } : t
        ));
      }
    } catch {
      setError("Không thể xóa bình luận");
    }
  }, [selectedTopic]);

  const goBackToList = useCallback(() => {
    setSelectedTopic(null);
  }, []);

  return {
    activeTab, setActiveTab,
    topics, topicsLoading, hasMoreTopics,
    categoryFilter, changeCategory,
    selectedTopic, topicLoading,
    showComposer, setShowComposer,
    submitting, error, setError,
    loadTopics, loadMoreTopics,
    loadTopicDetail, goBackToList,
    handleCreateTopic, handleDeleteTopic,
    handleCreateReply, handleDeleteReply,
  };
}
